// ============================================================================
// TEAM MEMBER OVERVIEW TAB
// Profile card with KPIs, employment details, and quick stats.
// Rendered inside WorkItemDetailModal when item_type === 'team_member'.
// Date: March 2026
// ============================================================================

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/team-member-detail.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/team-member-detail#profile)
 */

'use client';

import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import {
  User, Mail, Phone, MapPin, Briefcase, Calendar,
  Clock, DollarSign, TrendingUp, AlertTriangle, Star,
  Shield, Activity, Building2, ExternalLink,
  Heart, Car, Hash, Navigation, Loader2, RotateCcw, BellRing,
  PlayCircle, PauseCircle, CheckCircle2, CalendarClock,
  ClipboardCheck, UserCheck
} from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import { authService } from '@/lib/authService';
import { apiService } from '@/lib/apiService';
import { logUiAudit } from '@/lib/audit/uiAudit';
import { notify } from '@/lib/notifications';
import { useWorkItemOpener } from '@/hooks/useWorkItemOpener';
import UserDocsLink from '@/components/shared/UserDocsLink';
import { formatAdminAppointmentOverrideSuccessMessage, prepareAdminAppointmentOverride } from '@/lib/work-items/adminAppointmentOverrideClient';
import {
  getAllowedAppointmentAdminOverrideStatuses,
} from '@/lib/work-items/appointmentAdminOverrideTransitions';
import { getWorkItemTypeRegistry } from '@/config/work-items/configLoader';
import { effectiveTimeEntryHours } from '@/lib/analytics/productivityAnalyticsAdapter';
import { useTeamMemberOperationsRealtime, type TeamMemberLiveLocationUpdate } from './useTeamMemberOperationsRealtime';

const TeamMemberLocationMap = dynamic(() => import('./TeamMemberLocationMap'), { ssr: false });
import { getTeamMemberInspectionStatus } from '@/app/client-zone/modules/total-operations/components/smart-task/utils/vehicleInspectionStatus';
import SmartActionButton, { type SmartActionButtonState, type SmartActionMenuItem } from '@/app/client-zone/modules/total-operations/components/smart-task/components/SmartActionButton';

interface TeamMemberOverviewTabProps {
  workItem: WorkItem;
  isDark: boolean;
  refreshKey?: number;
  isActive?: boolean;
}

interface TeamMemberLicenseSummary extends UnknownRecord {
  total?: number;
  expired?: number;
  expiring_soon?: number;
  driving?: number;
}

interface TeamMemberLicenseRecord extends UnknownRecord {
  id?: number | string;
  license_type?: string | null;
  license_number?: string | null;
  type_label?: string | null;
  expiry_date?: string | null;
  status?: string | null;
}

type UnknownRecord = Record<string, unknown>;

interface TeamMemberCurrentItemSummary extends UnknownRecord {
  id?: string | null;
  item_type?: string | null;
  item_number?: string | null;
  display_number?: string | null;
  title?: string | null;
  status?: string | null;
  parent_job_number?: string | null;
  parent_job_title?: string | null;
  location_name?: string | null;
  location_address?: string | null;
}

interface TeamMemberCurrentOperations extends UnknownRecord {
  workflow_state?: string | null;
  workflow_state_label?: string | null;
  state_started_at?: string | null;
  travel_started_at?: string | null;
  work_started_at?: string | null;
  headline?: string | null;
  detail?: string | null;
  attendance_status?: string | null;
  attendance_status_label?: string | null;
  clocked_in_at?: string | null;
  on_break_since?: string | null;
  clock_in_location?: string | null;
  current_vehicle_registration?: string | null;
  current_vehicle_id?: string | null;
  is_non_driver?: boolean | null;
  travel_tracking_enabled?: boolean | null;
  current_item?: TeamMemberCurrentItemSummary | null;
  active_item?: TeamMemberCurrentItemSummary | null;
  travel_target_item?: TeamMemberCurrentItemSummary | null;
  travel?: {
    from_label?: string | null;
    to_label?: string | null;
    estimated_distance_km?: number | null;
    estimated_eta_minutes?: number | null;
    started_at?: string | null;
  } | null;
  is_field_worker?: boolean | null;
  show_last_known_location?: boolean | null;
  location_visible?: boolean | null;
  last_known_location?: {
    address?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    accuracy?: number | null;
    heading?: number | null;
    speed?: number | null;
    activity_type?: string | null;
    updated_at?: string | null;
  } | null;
}

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseCustomFields(value: unknown): Record<string, any> {
  if (!value) return {};

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return isRecord(parsed) ? parsed as Record<string, any> : {};
    } catch {
      return {};
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return isRecord(value) ? value as Record<string, any> : {};
}

function toNonEmptyString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return null;
}

function toFiniteNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// The HTTP fallback in /api/v1/field-workers/location historically stored this
// placeholder instead of a real address — treat it as "no address" so the UI
// falls back to a reverse-geocoded label or raw coordinates.
function isPlaceholderLocationAddress(value: string | null): boolean {
  return Boolean(value) && value!.toLowerCase().startsWith('on-demand gps capture');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getTeamMemberUserId(workItem: WorkItem, customFields: Record<string, any>): number | null {
  const customFieldUserId = Number(customFields.user_id);
  if (Number.isFinite(customFieldUserId) && customFieldUserId > 0) {
    return customFieldUserId;
  }

  const assignedTo = Number(workItem.assigned_to);
  if (Number.isFinite(assignedTo) && assignedTo > 0) {
    return assignedTo;
  }

  const idMatch = typeof workItem.id === 'string'
    ? workItem.id.match(/team-member-(\d+)/i)
    : null;

  if (idMatch) {
    const parsed = Number(idMatch[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  return null;
}

function formatItemTypeLabel(itemType: unknown): string {
  const normalized = toNonEmptyString(itemType);
  if (!normalized) return 'Work Item';
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatStatusLabel(status: unknown, itemType?: string | null): string {
  const normalized = toNonEmptyString(status);
  if (!normalized) return 'Unknown';
  if (itemType) {
    try {
      const registry = getWorkItemTypeRegistry();
      const label = registry.getStatusLabel(itemType, normalized);
      if (label) return label;
    } catch { /* fallback below */ }
  }
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDateTime(value: unknown): string | null {
  const normalized = toNonEmptyString(value);
  if (!normalized) return null;

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDurationSince(value: unknown): string | null {
  const normalized = toNonEmptyString(value);
  if (!normalized) return null;

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;

  const diffMs = Date.now() - date.getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return null;

  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}

function normalizeWorkflowState(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

type TeamMemberSmartStatus = 'scheduled' | 'on_route' | 'on_site' | 'in_progress' | 'paused' | 'resolved' | 'rescheduled';

const TEAM_MEMBER_SMART_ACTIONS: Array<{
  value: TeamMemberSmartStatus;
  label: string;
  icon: React.ElementType;
  tone: NonNullable<SmartActionMenuItem['tone']>;
}> = [
  { value: 'scheduled', label: 'Reset to scheduled', icon: RotateCcw, tone: 'slate' },
  { value: 'on_route', label: 'Set on route', icon: Navigation, tone: 'blue' },
  { value: 'on_site', label: 'Set on site', icon: MapPin, tone: 'cyan' },
  { value: 'in_progress', label: 'Start in progress', icon: PlayCircle, tone: 'emerald' },
  { value: 'paused', label: 'Pause in progress', icon: PauseCircle, tone: 'amber' },
  { value: 'resolved', label: 'End as resolved', icon: CheckCircle2, tone: 'emerald' },
  { value: 'rescheduled', label: 'Reschedule', icon: CalendarClock, tone: 'red' },
];

function normalizeAppointmentStatus(value: unknown): TeamMemberSmartStatus | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!normalized) {
    return null;
  }

  if (normalized === 'en_route') return 'on_route';
  if (normalized === 'arrived') return 'on_site';
  if (normalized === 'checked_in' || normalized === 'working') return 'in_progress';
  if (normalized === 'on_hold') return 'paused';
  if (normalized === 'reschedule') return 'rescheduled';

  return TEAM_MEMBER_SMART_ACTIONS.some((action) => action.value === normalized as TeamMemberSmartStatus)
    ? normalized as TeamMemberSmartStatus
    : null;
}

function mapWorkflowStateToAppointmentStatus(value: unknown): TeamMemberSmartStatus | null {
  const normalized = normalizeWorkflowState(value);

  switch (normalized) {
    case 'traveling':
    case 'travel_paused':
      return 'on_route';
    case 'arrived':
      return 'on_site';
    case 'checked_in':
      return 'in_progress';
    case 'paused':
      return 'paused';
    case 'idle':
    case 'ready':
    case 'vehicle_required':
    case 'inspection_pending':
      return 'scheduled';
    case 'completing':
      return 'resolved';
    default:
      return null;
  }
}

function formatSmartTaskStatusLabel(value: TeamMemberSmartStatus | null): string {
  if (!value) {
    return 'Waiting for live context';
  }

  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function getSmartTaskActionButtonState(value: TeamMemberSmartStatus | null): SmartActionButtonState {
  if (value === 'paused') {
    return 'paused';
  }

  if (value === 'scheduled' || value === 'on_site') {
    return 'resumable';
  }

  return 'default';
}

function getSmartTaskStatusRibbon(value: TeamMemberSmartStatus | null, isDark: boolean) {
  switch (value) {
    case 'in_progress':
      return {
        label: 'Working live',
        detail: 'Smart Action is actively tracking live work on the current appointment.',
        icon: Activity,
        borderClassName: isDark ? 'border-emerald-900/60' : 'border-emerald-200',
        backgroundClassName: isDark ? 'bg-emerald-950/50' : 'bg-emerald-50',
        textClassName: isDark ? 'text-emerald-300' : 'text-emerald-700',
        detailClassName: isDark ? 'text-emerald-200/80' : 'text-emerald-800',
      };
    case 'paused':
      return {
        label: 'Paused by workflow',
        detail: 'Use Smart Action to resume progress when the team member is ready to continue.',
        icon: PauseCircle,
        borderClassName: isDark ? 'border-amber-900/60' : 'border-amber-200',
        backgroundClassName: isDark ? 'bg-amber-950/50' : 'bg-amber-50',
        textClassName: isDark ? 'text-amber-300' : 'text-amber-700',
        detailClassName: isDark ? 'text-amber-200/80' : 'text-amber-800',
      };
    case 'on_route':
      return {
        label: 'Travelling to appointment',
        detail: 'The worker is on the move. Smart Action can push them to on-site or in-progress when they arrive.',
        icon: Navigation,
        borderClassName: isDark ? 'border-blue-900/60' : 'border-blue-200',
        backgroundClassName: isDark ? 'bg-blue-950/50' : 'bg-blue-50',
        textClassName: isDark ? 'text-blue-300' : 'text-blue-700',
        detailClassName: isDark ? 'text-blue-200/80' : 'text-blue-800',
      };
    case 'on_site':
      return {
        label: 'On site and ready',
        detail: 'The team member has arrived. Use Smart Action to begin the live work session.',
        icon: MapPin,
        borderClassName: isDark ? 'border-cyan-900/60' : 'border-cyan-200',
        backgroundClassName: isDark ? 'bg-cyan-950/50' : 'bg-cyan-50',
        textClassName: isDark ? 'text-cyan-300' : 'text-cyan-700',
        detailClassName: isDark ? 'text-cyan-200/80' : 'text-cyan-800',
      };
    case 'resolved':
      return {
        label: 'Appointment resolved',
        detail: 'This appointment was ended from SmartTask or an admin override and is now in a terminal state.',
        icon: CheckCircle2,
        borderClassName: isDark ? 'border-emerald-900/60' : 'border-emerald-200',
        backgroundClassName: isDark ? 'bg-emerald-950/50' : 'bg-emerald-50',
        textClassName: isDark ? 'text-emerald-300' : 'text-emerald-700',
        detailClassName: isDark ? 'text-emerald-200/80' : 'text-emerald-800',
      };
    case 'rescheduled':
      return {
        label: 'Appointment rescheduled',
        detail: 'The current appointment context has been moved, so Smart Action is waiting for the next authoritative workflow state.',
        icon: CalendarClock,
        borderClassName: isDark ? 'border-rose-900/60' : 'border-rose-200',
        backgroundClassName: isDark ? 'bg-rose-950/50' : 'bg-rose-50',
        textClassName: isDark ? 'text-rose-300' : 'text-rose-700',
        detailClassName: isDark ? 'text-rose-200/80' : 'text-rose-800',
      };
    case 'scheduled':
    default:
      return {
        label: 'Ready for SmartTask action',
        detail: 'Use Smart Action to start travel, mark arrival, begin work, or apply an admin override for this live appointment.',
        icon: Activity,
        borderClassName: isDark ? 'border-slate-800' : 'border-slate-200',
        backgroundClassName: isDark ? 'bg-slate-900/70' : 'bg-slate-50',
        textClassName: isDark ? 'text-slate-300' : 'text-slate-700',
        detailClassName: isDark ? 'text-slate-400' : 'text-slate-500',
      };
  }
}

function WorkflowMetricCard({
  label,
  value,
  detail,
  isDark,
}: {
  label: string;
  value: string;
  detail?: string | null;
  isDark: boolean;
}) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
      <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        {label}
      </p>
      <p className={`mt-1 text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
        {value}
      </p>
      {detail ? (
        <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {detail}
        </p>
      ) : null}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subValue, color, isDark }: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  subValue?: string;
  color: string;
  isDark: boolean;
}) {
  return (
    <div className={`p-4 rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
          <p className={`text-lg font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{value}</p>
          {subValue && (
            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{subValue}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, isDark }: {
  icon: React.ElementType;
  label: string;
  value: string | null | undefined;
  isDark: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
      <div className="min-w-0 flex-1">
        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{label}</p>
        <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{value}</p>
      </div>
    </div>
  );
}

export default function TeamMemberOverviewTab({ workItem, isDark, isActive = true }: TeamMemberOverviewTabProps) {
  const [smartActionMenuOpen, setSmartActionMenuOpen] = useState(false);
  const { openWorkItem } = useWorkItemOpener();

  useEffect(() => {
    if (!isActive) return;
    void logUiAudit({
      component: 'TeamMemberOverviewTab',
      action: 'view',
      moduleKey: 'total-operations',
      workItemId: workItem.id ? String(workItem.id) : undefined,
      metadata: { item_type: workItem.item_type },
    });
  }, [isActive, workItem.id, workItem.item_type]);

  const initialCustomFields = useMemo(() => parseCustomFields(workItem.custom_fields), [workItem.custom_fields]);
  const teamMemberUserId = useMemo(
    () => getTeamMemberUserId(workItem, initialCustomFields),
    [initialCustomFields, workItem],
  );
  const [liveWorkItem, setLiveWorkItem] = useState<WorkItem | null>(null);
  const [isRefreshingProfile, setIsRefreshingProfile] = useState(false);
  // Job manager eligibility — visible only to root/owner/system_admin
  // (server reports can_manage); flags the member's Total Operations
  // module_access custom_permissions.can_be_work_item_manager.
  const [managerEligibility, setManagerEligibility] = useState<{ canManage: boolean; eligible: boolean; saving: boolean; error: string | null }>({
    canManage: false,
    eligible: false,
    saving: false,
    error: null,
  });
  useEffect(() => {
    if (!teamMemberUserId) return;
    const token = authService.getTokens()?.access_token;
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch('/api/v1/work-items/eligible-managers', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json();
        if (cancelled || !payload?.success) return;
        const managers: Array<{ id: number | string }> = payload?.data?.managers || [];
        setManagerEligibility((prev) => ({
          ...prev,
          canManage: Boolean(payload?.data?.can_manage),
          eligible: managers.some((m) => Number(m.id) === teamMemberUserId),
        }));
      } catch { /* optional surface */ }
    })();
    return () => { cancelled = true; };
  }, [teamMemberUserId]);

  const toggleManagerEligibility = useCallback(async (enabled: boolean) => {
    if (!teamMemberUserId) return;
    const token = authService.getTokens()?.access_token;
    if (!token) return;
    setManagerEligibility((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const response = await fetch('/api/v1/operations/field-workers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          action: 'set_manager_eligibility',
          user_id: teamMemberUserId,
          can_be_work_item_manager: enabled,
        }),
      });
      const payload = await response.json();
      if (response.ok && payload?.success) {
        setManagerEligibility((prev) => ({ ...prev, eligible: enabled, saving: false }));
      } else {
        setManagerEligibility((prev) => ({
          ...prev,
          saving: false,
          error: payload?.error || 'Failed to update manager eligibility',
        }));
      }
    } catch {
      setManagerEligibility((prev) => ({ ...prev, saving: false, error: 'Failed to update manager eligibility' }));
    }
  }, [teamMemberUserId]);
  const [timeEntriesData, setTimeEntriesData] = useState<{
    total_hours: number;
    billable_hours: number;
  } | null>(null);

  const loadTimeEntries = useCallback(async () => {
    if (!teamMemberUserId) return;

    const token = authService.getTokens()?.access_token;
    if (!token) return;

    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

      const response = await fetch(
        `/api/v1/time-entries?user_id=${teamMemberUserId}&start_date=${startOfMonth}&end_date=${endOfMonth}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) return;

      const result = await response.json();
      if (!result?.success || !result?.data) return;

      // Cap-applied hours — unreviewed multi-day zombie timers are trimmed to
      // a sane shift so they don't inflate the headline hours.
      const entries = result.data || [];
      const totalHours = entries.reduce((sum: number, entry: Parameters<typeof effectiveTimeEntryHours>[0]) => {
        return sum + effectiveTimeEntryHours(entry);
      }, 0);

      const billableHours = entries.reduce((sum: number, entry: Parameters<typeof effectiveTimeEntryHours>[0] & { is_billable?: boolean }) => {
        if (entry.is_billable) {
          return sum + effectiveTimeEntryHours(entry);
        }
        return sum;
      }, 0);

      setTimeEntriesData({ total_hours: totalHours, billable_hours: billableHours });
    } catch (error) {
      console.error('[TeamMemberOverviewTab] Failed to load time entries:', error);
    }
  }, [teamMemberUserId]);

  const refreshLiveProfile = useCallback(async () => {
    if (!teamMemberUserId) {
      return;
    }

    setIsRefreshingProfile(true);
    try {
      const payload = await apiService.get<{ success: boolean; data?: WorkItem }>(
        `/api/v1/team/members/${teamMemberUserId}/profile`
      );
      if (!payload?.success || !payload?.data) {
        return;
      }

      setLiveWorkItem(payload.data as WorkItem);
    } catch (error) {
      console.error('[TeamMemberOverviewTab] Failed to refresh live profile:', error);
    } finally {
      setIsRefreshingProfile(false);
    }
  }, [teamMemberUserId]);

  useEffect(() => {
    if (isActive) {
      void loadTimeEntries();
    }
  }, [isActive, loadTimeEntries]);

  useEffect(() => {
    void refreshLiveProfile();
  }, [refreshLiveProfile]);

  // Intentionally no mount/refreshKey auto-fetch: the parent WorkItemDetailModal
  // already hydrates currentWorkItem via its own profile fetch and passes the
  // result as the `workItem` prop here. Only WebSocket events trigger a live
  // re-fetch via handleRealtimeRefresh → refreshLiveProfile.

  const effectiveWorkItem = liveWorkItem ?? workItem;
  const cf = useMemo(
    () => liveWorkItem ? parseCustomFields(liveWorkItem.custom_fields) : initialCustomFields,
    [liveWorkItem, initialCustomFields],
  );
  const currentOperations = useMemo(() => {
    const value = cf.current_operations;
    return isRecord(value) ? value as TeamMemberCurrentOperations : null;
  }, [cf.current_operations]);

  // ── Driver designation (SSOT: member's resolved IAM permissions) ───────
  // Config 1 driver: vehicle selection + inspections apply.
  // Config 2 non-driver + travel tracking: no vehicle/inspection UI, but
  // travel time is still monitored.
  // Config 3 non-driver + no travel: appointment check-in only.
  const isNonDriver = currentOperations?.is_non_driver === true;
  const travelTrackingEnabled = currentOperations?.travel_tracking_enabled !== false;

  // ── SSOT: Vehicle allocation from vehicle_driver_allocations ──
  // Instead of relying on the stale currentOperations.current_vehicle_*
  // custom fields, query the allocation table directly for the driver's
  // active vehicle. Falls back to currentOperations when no allocation
  // is found (e.g. non-fleet workflows).
  const [ssotVehicle, setSsotVehicle] = useState<{
    vehicleId: string | null;
    vehicleRegistration: string | null;
  } | null>(null);

  const refreshSsotVehicle = useCallback(async () => {
    if (!teamMemberUserId) {
      setSsotVehicle(null);
      return;
    }
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;
      const res = await fetch('/api/v1/work-items/fleet', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getDriverCurrentAllocation',
          userId: teamMemberUserId,
        }),
      });
      if (!res.ok) return;
      const payload = await res.json();
      if (payload?.success && payload?.data?.hasAllocation && payload?.data?.allocation?.vehicle) {
        const v = payload.data.allocation.vehicle;
        setSsotVehicle({
          vehicleId: toNonEmptyString(v.id) || toNonEmptyString(v.external_id),
          vehicleRegistration: toNonEmptyString(v.registration) || toNonEmptyString(v.title),
        });
      } else {
        setSsotVehicle(null);
      }
    } catch {
      // Non-fleet environments may not have the endpoint — silently fall back
      setSsotVehicle(null);
    }
  }, [teamMemberUserId]);

  useEffect(() => {
    // Non-drivers never hold fleet allocations — skip the lookup entirely.
    if (isNonDriver) {
      setSsotVehicle(null);
      return;
    }
    void refreshSsotVehicle();
  }, [refreshSsotVehicle, isNonDriver]);

  // ── Mobile warehouse (van) summary — item count, units, and how many
  // lines are still provisional field purchases. Powers the "Van stock"
  // metric card; the full item detail lives on the Van Stock tab.
  const [vanStockSummary, setVanStockSummary] = useState<{
    warehouseName: string;
    itemCount: number;
    totalQuantity: number;
    provisionalCount: number;
  } | null>(null);

  useEffect(() => {
    if (!teamMemberUserId || !isActive) {
      setVanStockSummary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = authService.getTokens()?.access_token;
        if (!token) return;
        const res = await fetch(
          `/api/v1/work-items/inventory/member-mobile-warehouse?user_id=${teamMemberUserId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) return;
        const payload = await res.json();
        const warehouse = payload?.data?.warehouse;
        if (cancelled || !warehouse) {
          if (!cancelled) setVanStockSummary(null);
          return;
        }
        const items = Array.isArray(payload?.data?.stock_items) ? payload.data.stock_items : [];
        setVanStockSummary({
          warehouseName: toNonEmptyString(warehouse.name) || 'Van',
          itemCount: items.length,
          totalQuantity: items.reduce((sum: number, i: { quantity_on_hand?: number }) => sum + (Number(i.quantity_on_hand) || 0), 0),
          provisionalCount: items.filter((i: { provisional?: { pending_confirmation?: number } | null }) => (i.provisional?.pending_confirmation ?? 0) > 0).length,
        });
      } catch {
        if (!cancelled) setVanStockSummary(null);
      }
    })();
    return () => { cancelled = true; };
  }, [teamMemberUserId, isActive]);

  const currentItem = isRecord(currentOperations?.current_item)
    ? currentOperations?.current_item as TeamMemberCurrentItemSummary
    : null;
  const travel = currentOperations?.travel && isRecord(currentOperations.travel)
    ? currentOperations.travel
    : null;
  const stateStartedAt = toNonEmptyString(currentOperations?.state_started_at)
    || toNonEmptyString(currentOperations?.travel_started_at)
    || toNonEmptyString(currentOperations?.work_started_at);
  const activeFor = formatDurationSince(stateStartedAt);
  const stateStartedDisplay = formatDateTime(stateStartedAt);
  const travelStartedDisplay = formatDateTime(currentOperations?.travel_started_at);
  const workStartedDisplay = formatDateTime(currentOperations?.work_started_at);
  const breakStartedDisplay = formatDateTime(currentOperations?.on_break_since);
  const controllableAppointment = useMemo(() => {
    const appointmentCandidate = [
      currentOperations?.current_item,
      currentOperations?.active_item,
      currentOperations?.travel_target_item,
    ].find((item) => isRecord(item) && toNonEmptyString((item as TeamMemberCurrentItemSummary).item_type) === 'appointment' && toNonEmptyString((item as TeamMemberCurrentItemSummary).id));

    return appointmentCandidate && isRecord(appointmentCandidate)
      ? appointmentCandidate as TeamMemberCurrentItemSummary
      : null;
  }, [currentOperations?.active_item, currentOperations?.current_item, currentOperations?.travel_target_item]);
  const licenseSummary = useMemo(() => {
    return isRecord(cf.license_summary) ? cf.license_summary as TeamMemberLicenseSummary : null;
  }, [cf.license_summary]);
  const licenses = useMemo(() => {
    return Array.isArray(cf.licenses) ? cf.licenses.filter(isRecord) as TeamMemberLicenseRecord[] : [];
  }, [cf.licenses]);
  const primaryDriverLicense = useMemo(() => {
    return licenses.find((license) => license.license_type === 'drivers_license' || license.license_type === 'heavy_vehicle') || null;
  }, [licenses]);
  const appointmentWorkflowContext = useMemo(() => {
    if (!controllableAppointment?.id || !teamMemberUserId) {
      return null;
    }

    const [firstName, ...restName] = (effectiveWorkItem.title || '').trim().split(/\s+/).filter(Boolean);
    const lastName = restName.join(' ');

    return {
      id: controllableAppointment.id,
      status: controllableAppointment.status || undefined,
      assigned_to: teamMemberUserId,
      assigned_to_name: effectiveWorkItem.title || cf.email || `User ${teamMemberUserId}`,
      assignee_name: effectiveWorkItem.title || cf.email || `User ${teamMemberUserId}`,
      custom_fields: {},
      assignees: [
        {
          user_id: teamMemberUserId,
          first_name: firstName || null,
          last_name: lastName || null,
          email: cf.email || null,
        },
      ],
    };
  }, [cf.email, controllableAppointment?.id, controllableAppointment?.status, effectiveWorkItem.title, teamMemberUserId]);
  const controlledAppointmentStatus = useMemo(() => {
    return normalizeAppointmentStatus(appointmentWorkflowContext?.status)
      || normalizeAppointmentStatus(controllableAppointment?.status)
      || mapWorkflowStateToAppointmentStatus(currentOperations?.workflow_state)
      || null;
  }, [appointmentWorkflowContext?.status, controllableAppointment?.status, currentOperations?.workflow_state]);
  const smartTaskRibbon = useMemo(
    () => getSmartTaskStatusRibbon(controlledAppointmentStatus, isDark),
    [controlledAppointmentStatus, isDark],
  );
  const smartTaskActionButtonState = useMemo(
    () => getSmartTaskActionButtonState(controlledAppointmentStatus),
    [controlledAppointmentStatus],
  );
  const smartTaskControlStatus = formatSmartTaskStatusLabel(controlledAppointmentStatus)
    || toNonEmptyString(currentOperations?.workflow_state_label)
    || formatStatusLabel(currentOperations?.workflow_state, workItem.item_type);
  const smartTaskControlAssignment = controllableAppointment
    ? `${formatItemTypeLabel(controllableAppointment.item_type)} · ${toNonEmptyString(controllableAppointment.display_number) || toNonEmptyString(controllableAppointment.item_number) || 'Live item'}`
    : 'No live appointment selected';
  const smartTaskControlOverrideState = appointmentWorkflowContext?.status
    ? formatStatusLabel(appointmentWorkflowContext.status, workItem.item_type)
    : 'Waiting for live appointment context';
  const allowedSmartTaskOverrideStatuses = useMemo<TeamMemberSmartStatus[]>(() => {
    return getAllowedAppointmentAdminOverrideStatuses(
      appointmentWorkflowContext?.status
      || controllableAppointment?.status
      || currentOperations?.workflow_state,
    ).filter((status): status is TeamMemberSmartStatus => TEAM_MEMBER_SMART_ACTIONS.some((action) => action.value === status));
  }, [appointmentWorkflowContext?.status, controllableAppointment?.status, currentOperations?.workflow_state]);
  // SSOT: prefer vehicle_driver_allocations over stale custom fields.
  // Falls back to currentOperations.current_vehicle_* when no active
  // allocation is found (e.g. non-fleet workflows or transient states).
  const currentVehicleRegistration = ssotVehicle?.vehicleRegistration
    || toNonEmptyString(currentOperations?.current_vehicle_registration);
  const currentVehicleId = ssotVehicle?.vehicleId
    || toNonEmptyString(currentOperations?.current_vehicle_id);

  // ── Inspection status (SSOT) ──────────────────────────────────────
  // Uses the same shared function as TeamMemberSmartTaskTab and
  // TeamMemberVehicleInspectionsTab so all admin tabs show the same status.
  const inspectionStatus = useMemo(() => {
    return getTeamMemberInspectionStatus(currentOperations);
  }, [currentOperations]);
  const attendanceStatusLabel = toNonEmptyString(currentOperations?.attendance_status_label)
    || formatStatusLabel(currentOperations?.attendance_status, workItem.item_type);
  const currentItemLocation = toNonEmptyString(currentItem?.location_name)
    || toNonEmptyString(currentItem?.location_address);
  const travelFromLabel = toNonEmptyString(travel?.from_label);
  const travelToLabel = toNonEmptyString(travel?.to_label);
  const currentItemReference = [
    toNonEmptyString(currentItem?.display_number) || toNonEmptyString(currentItem?.item_number),
    toNonEmptyString(currentItem?.parent_job_number),
  ].filter((value): value is string => Boolean(value)).join(' · ');
  // ── Live worker location ─────────────────────────────────────────────
  // Profile data carries last_known_location (from field_worker_locations);
  // realtime pushes on location:client:{id} and worker:location_update events
  // update liveLocation between profile refreshes.
  // Location is a privacy boundary: the server only returns it (and sets
  // location_visible) for field workers — is_field_worker is the single
  // trackability flag.
  const locationVisible = currentOperations?.location_visible === true;
  const [liveLocation, setLiveLocation] = useState<TeamMemberLiveLocationUpdate | null>(null);
  const [geocodedLocationAddress, setGeocodedLocationAddress] = useState<string | null>(null);
  const geocodedLocationKeyRef = useRef<string | null>(null);
  const handleTeamMemberLocationUpdate = useCallback((update: TeamMemberLiveLocationUpdate) => {
    setLiveLocation(update);
  }, []);

  useEffect(() => {
    setLiveLocation(null);
    setGeocodedLocationAddress(null);
    geocodedLocationKeyRef.current = null;
  }, [teamMemberUserId, locationVisible]);

  const lastKnownLocationRecord = currentOperations?.last_known_location && isRecord(currentOperations.last_known_location)
    ? currentOperations.last_known_location
    : null;
  const profileLocationLatitude = toFiniteNumber(lastKnownLocationRecord?.latitude);
  const profileLocationLongitude = toFiniteNumber(lastKnownLocationRecord?.longitude);
  const profileLocationUpdatedAt = toNonEmptyString(lastKnownLocationRecord?.updated_at);

  const liveLocationIsNewer = Boolean(liveLocation)
    && (!profileLocationUpdatedAt
      || new Date(liveLocation!.updatedAt).getTime() >= new Date(profileLocationUpdatedAt).getTime());
  const effectiveLocationLatitude = liveLocationIsNewer ? liveLocation!.latitude : profileLocationLatitude;
  const effectiveLocationLongitude = liveLocationIsNewer ? liveLocation!.longitude : profileLocationLongitude;
  const effectiveLocationAccuracy = liveLocationIsNewer ? liveLocation!.accuracy : toFiniteNumber(lastKnownLocationRecord?.accuracy);
  const effectiveLocationHeading = liveLocationIsNewer ? liveLocation!.heading : toFiniteNumber(lastKnownLocationRecord?.heading);
  const effectiveLocationUpdatedAt = liveLocationIsNewer ? liveLocation!.updatedAt : profileLocationUpdatedAt;
  const hasEffectiveLocation = locationVisible && effectiveLocationLatitude != null && effectiveLocationLongitude != null;

  const storedLocationAddress = toNonEmptyString(lastKnownLocationRecord?.address);
  const lastKnownLocationAddress = storedLocationAddress && !isPlaceholderLocationAddress(storedLocationAddress)
    ? storedLocationAddress
    : null;
  const lastKnownLocationUpdatedAt = formatDateTime(effectiveLocationUpdatedAt);

  // Reverse-geocode a display label when the stored row has coordinates but no
  // real address (WS/transition writes don't set `address`).
  useEffect(() => {
    if (lastKnownLocationAddress || !hasEffectiveLocation) {
      return;
    }

    const lat = effectiveLocationLatitude!;
    const lng = effectiveLocationLongitude!;
    // ~100m granularity — don't re-geocode on every GPS ping.
    const key = `${lat.toFixed(3)},${lng.toFixed(3)}`;
    if (geocodedLocationKeyRef.current === key) {
      return;
    }
    geocodedLocationKeyRef.current = key;

    const token = authService.getTokens()?.access_token;
    if (!token) {
      return;
    }

    let cancelled = false;
    fetch(`/api/v1/geocode?lat=${lat}&lng=${lng}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (!cancelled && payload?.success && typeof payload.display_name === 'string' && payload.display_name.trim()) {
          setGeocodedLocationAddress(payload.display_name.trim());
        }
      })
      .catch(() => { /* reverse geocode is best-effort */ });

    return () => {
      cancelled = true;
    };
  }, [lastKnownLocationAddress, hasEffectiveLocation, effectiveLocationLatitude, effectiveLocationLongitude]);

  const lastKnownLocationDisplay = lastKnownLocationAddress
    || geocodedLocationAddress
    || (hasEffectiveLocation
      ? `${effectiveLocationLatitude!.toFixed(5)}, ${effectiveLocationLongitude!.toFixed(5)}`
      : null);

  const handleOpenVehicle = useCallback(() => {
    if (!currentVehicleId) {
      return;
    }

    openWorkItem(currentVehicleId);
  }, [currentVehicleId, openWorkItem]);

  // ── Admin Inspection Override ─────────────────────────────────────
  // Admins can override the inspection_pending state for a team member,
  // authorizing them to skip the take-on inspection. This calls the same
  // backend `override_inspection` action as SmartTaskShell's
  // handleAuthorizeInspectionOverride, targeting the team member's user ID.
  const [inspectionOverrideBusy, setInspectionOverrideBusy] = useState(false);

  const handleAuthorizeInspectionOverride = useCallback(async () => {
    if (!teamMemberUserId || !currentVehicleId) return;
    setInspectionOverrideBusy(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        notify.error('Authentication required');
        return;
      }
      const res = await fetch('/api/v1/work-items/workflow-state', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'override_inspection',
          vehicle_id: currentVehicleId,
          vehicle_registration: currentVehicleRegistration || undefined,
          metadata: {
            target_user_id: teamMemberUserId,
            reason: 'Admin override from team member overview',
          },
        }),
      });
      if (res.status === 403) {
        notify.error('Only admins can override inspection requirements.');
        return;
      }
      if (res.status === 409) {
        notify.warning('An inspection override already exists for this vehicle.', 5000);
        return;
      }
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        notify.error(payload?.error || 'Failed to authorize inspection override');
        return;
      }
      notify.success('Inspection override authorized. The driver can now skip the inspection.');
      // Refresh the profile to update the inspection status
      void refreshLiveProfile();
    } catch {
      notify.error('Failed to authorize inspection override');
    } finally {
      setInspectionOverrideBusy(false);
    }
  }, [teamMemberUserId, currentVehicleId, currentVehicleRegistration, refreshLiveProfile]);

  const handleSmartTaskOverride = useCallback(async (requestedStatus: TeamMemberSmartStatus) => {
    if (!appointmentWorkflowContext?.id || !teamMemberUserId) {
      return;
    }

    const currentAppointmentStatus = appointmentWorkflowContext?.status
      || controllableAppointment?.status
      || currentOperations?.workflow_state
      || null;

    const token = authService.getTokens()?.access_token;
    if (!token) {
      notify.error('Authentication expired. Please sign in again.');
      return;
    }

    try {
      const preparedOverride = await prepareAdminAppointmentOverride({
        authToken: token,
        workItemId: appointmentWorkflowContext.id,
        requestedStatus,
        currentStatus: currentAppointmentStatus,
        subjectLabel: effectiveWorkItem.title || `User ${teamMemberUserId}`,
        targetUserId: teamMemberUserId,
      });

      if (!preparedOverride) {
        return;
      }

      const response = await fetch('/api/v1/work-items/workflow-state', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preparedOverride.requestBody),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Failed to update SmartTask status');
      }

      notify.success(formatAdminAppointmentOverrideSuccessMessage({
        requestedStatus,
        successContext: preparedOverride.successContext,
      }));
      void refreshLiveProfile();
    } catch (error) {
      console.error('[TeamMemberOverviewTab] Failed to apply SmartTask override:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to update SmartTask status');
    }
  }, [appointmentWorkflowContext?.id, appointmentWorkflowContext?.status, controllableAppointment?.status, currentOperations?.workflow_state, effectiveWorkItem.title, refreshLiveProfile, teamMemberUserId]);

  // ── On-demand GPS request ─────────────────────────────────────────────
  // Admin can request the worker's current location. This publishes a
  // realtime event to the worker's device, which captures GPS and submits
  // it via the WS `location_update` message (or /api/v1/field-workers/location
  // as fallback). The resulting location:client push updates the marker
  // instantly; the delayed profile refresh below is a safety net.
  //
  // Two modes:
  //   - Silent (default): realtime event only — invisible to the worker.
  //     Works while their app has an active WebSocket (on duty).
  //   - Wake-up (send_push): also sends an FCM notification that wakes a
  //     backgrounded/killed app, which auto-captures GPS on receipt/tap.
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [isRequestingLocationWakeUp, setIsRequestingLocationWakeUp] = useState(false);

  const handleRequestLocation = useCallback(async (sendPush = false) => {
    if (!teamMemberUserId) {
      return;
    }

    const token = authService.getTokens()?.access_token;
    if (!token) {
      notify.error('Authentication expired. Please sign in again.');
      return;
    }

    const setBusy = sendPush ? setIsRequestingLocationWakeUp : setIsRequestingLocation;
    setBusy(true);
    try {
      const response = await fetch(`/api/v1/team/members/${teamMemberUserId}/admin-actions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'request_location', send_push: sendPush }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Failed to send location request');
      }

      notify.success(sendPush
        ? 'Location request sent with device wake-up notification'
        : 'Location request sent to worker device');
      // Refresh after a short delay to allow the worker's device to respond
      setTimeout(() => void refreshLiveProfile(), 5000);
    } catch (error) {
      console.error('[TeamMemberOverviewTab] Failed to request location:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to request location');
    } finally {
      setBusy(false);
    }
  }, [refreshLiveProfile, teamMemberUserId]);

  const smartTaskActionItems = useMemo<SmartActionMenuItem[]>(() => {
    if (!appointmentWorkflowContext?.id || !teamMemberUserId) {
      return [];
    }

    return TEAM_MEMBER_SMART_ACTIONS
      .filter((action) => action.value !== controlledAppointmentStatus && allowedSmartTaskOverrideStatuses.includes(action.value))
      .map((action) => ({
        key: action.value,
        label: action.label,
        icon: action.icon,
        tone: action.tone,
        executionMode: 'await' as const,
        onClick: async () => {
          await handleSmartTaskOverride(action.value);
        },
      }));
  }, [allowedSmartTaskOverrideStatuses, appointmentWorkflowContext?.id, controlledAppointmentStatus, handleSmartTaskOverride, teamMemberUserId]);

  const relevantAppointmentIds = useMemo(() => [
    String(controllableAppointment?.id || ''),
    String(currentItem?.id || ''),
    String(currentOperations?.active_item && isRecord(currentOperations.active_item)
      ? (currentOperations.active_item as TeamMemberCurrentItemSummary).id || ''
      : ''),
    String(currentOperations?.travel_target_item && isRecord(currentOperations.travel_target_item)
      ? (currentOperations.travel_target_item as TeamMemberCurrentItemSummary).id || ''
      : ''),
  ].filter(Boolean), [controllableAppointment?.id, currentItem?.id, currentOperations?.active_item, currentOperations?.travel_target_item]);

  useTeamMemberOperationsRealtime({
    teamMemberUserId,
    relevantAppointmentIds,
    enabled: isActive,
    locationUpdatesEnabled: locationVisible,
    onRelevantMessage: () => {
      void refreshLiveProfile();
      void refreshSsotVehicle();
    },
    onLocationUpdate: handleTeamMemberLocationUpdate,
  });

  const fmtCurrency = (val: number | null | undefined) => {
    if (!val && val !== 0) return '-';
    return `R ${val.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const fmtHours = (val: number | null | undefined) => {
    if (!val && val !== 0) return '0h';
    const h = Math.floor(val);
    const m = Math.round((val - h) * 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const employmentStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      on_leave: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      terminated: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      probation: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    };
    return map[status] || map.active;
  };

  const profileImage = cf.profile_image_url;
  const initials = (effectiveWorkItem.title || '')
    .split(' ')
    .map((w: string) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="space-y-6">
      {/* ── Profile Header ── */}
      <div className={`flex flex-col sm:flex-row items-start gap-4 p-5 rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        {/* Avatar */}
        <div className="flex-shrink-0">
          {profileImage ? (
            <img
              src={profileImage}
              alt={effectiveWorkItem.title}
              className="w-20 h-20 rounded-full object-cover border-2 border-orange-400"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-2xl font-bold border-2 border-orange-400">
              {initials || '?'}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className={`text-xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              {effectiveWorkItem.title}
            </h2>
            <UserDocsLink docsPath="/total-operations/workflows/service-operations/team-member-detail#profile" label="User guide: Team member profile" />
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${employmentStatusBadge(cf.employment_status || 'active')}`}>
              {(cf.employment_status || 'active').replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
            </span>
            {cf.role && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                {cf.role.replace(/\b\w/g, (l: string) => l.toUpperCase())}
              </span>
            )}
          </div>
          {cf.job_title && (
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {cf.job_title}
              {cf.department && ` · ${cf.department}`}
            </p>
          )}
          <div className="flex items-center gap-4 mt-2 flex-wrap">
            {cf.email && (
              <a href={`mailto:${cf.email}`} className="flex items-center gap-1 text-xs text-blue-500 hover:underline">
                <Mail className="w-3 h-3" /> {cf.email}
              </a>
            )}
            {cf.phone && (
              <a href={`tel:${cf.phone}`} className="flex items-center gap-1 text-xs text-blue-500 hover:underline">
                <Phone className="w-3 h-3" /> {cf.phone}
              </a>
            )}
          </div>
          {cf.manager_name && (
            <p className={`text-xs mt-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Reports to: <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{cf.manager_name}</span>
            </p>
          )}
          {isRefreshingProfile && (
            <div className={`mt-3 inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-medium ${isDark ? 'bg-gray-700/70 text-gray-200' : 'bg-white text-gray-600 border border-gray-200'}`}>
              <Loader2 className="w-3 h-3 animate-spin" /> Refreshing live SmartTask status
            </div>
          )}
        </div>
      </div>

      {/* ── KPI Grid ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={Briefcase}
          label="Active Work Items"
          value={cf.active_items_count ?? 0}
          subValue={`${cf.total_assigned_items ?? 0} total assigned`}
          color="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
          isDark={isDark}
        />
        <StatCard
          icon={Clock}
          label="Hours This Month"
          value={fmtHours(timeEntriesData?.total_hours ?? cf.total_hours_this_month)}
          subValue={`${fmtHours(timeEntriesData?.billable_hours ?? cf.billable_hours_this_month)} billable`}
          color="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
          isDark={isDark}
        />
        <StatCard
          icon={TrendingUp}
          label="Capacity Utilization"
          value={`${cf.capacity_utilization_pct ?? 0}%`}
          subValue="of available hours"
          color="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400"
          isDark={isDark}
        />
        <StatCard
          icon={DollarSign}
          label="Billable Income"
          value={fmtCurrency(cf.actual_billable_income)}
          subValue={`Projected: ${fmtCurrency(cf.projected_monthly_income)}`}
          color="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
          isDark={isDark}
        />
      </div>

      {/* ── Live SmartTask Snapshot ── */}
      <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <Activity className="mt-0.5 h-4 w-4 text-blue-500" />
            <div>
              <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                Live SmartTask snapshot
              </h3>
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                A tighter admin mirror of the field worker’s live appointment, vehicle, travel, and attendance state.
              </p>
            </div>
          </div>
          {appointmentWorkflowContext ? (
            <div className="w-full max-w-[260px]">
              <SmartActionButton
                items={smartTaskActionItems}
                label={smartTaskControlStatus}
                state={smartTaskActionButtonState}
                fullWidth
                menuPlacement="bottom"
                open={smartActionMenuOpen}
                onOpenChange={setSmartActionMenuOpen}
                icon={Activity}
                disabled={smartTaskActionItems.length === 0}
              />
            </div>
          ) : null}
        </div>

        <div className={`mt-4 rounded-xl border px-4 py-3 ${smartTaskRibbon.borderClassName} ${smartTaskRibbon.backgroundClassName}`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <smartTaskRibbon.icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${smartTaskRibbon.textClassName}`} />
              <div className="min-w-0">
                <p className={`text-sm font-semibold ${smartTaskRibbon.textClassName}`}>
                  {smartTaskRibbon.label}
                </p>
                <p className={`mt-1 text-xs ${smartTaskRibbon.detailClassName}`}>
                  {smartTaskRibbon.detail}
                </p>
              </div>
            </div>
            {(activeFor || stateStartedDisplay) ? (
              <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${smartTaskRibbon.textClassName} ${isDark ? 'bg-black/20' : 'bg-white/70'}`}>
                {activeFor ? `${activeFor} active` : `Since ${stateStartedDisplay}`}
              </span>
            ) : null}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {attendanceStatusLabel ? (
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
              <Clock className="h-3 w-3" />
              {attendanceStatusLabel}
            </span>
          ) : null}
          {isNonDriver ? (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700'}`}
              title="Non-driver — vehicle selection and inspections are handled by the driver"
            >
              <UserCheck className="h-3 w-3" />
              Non-driver
            </span>
          ) : null}
          {isNonDriver && !travelTrackingEnabled ? (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700'}`}
              title="Travel time is not tracked for this member"
            >
              <Navigation className="h-3 w-3" />
              Travel not tracked
            </span>
          ) : null}
          {!isNonDriver && currentVehicleRegistration ? (
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
              <Car className="h-3 w-3" />
              {currentVehicleRegistration}
            </span>
          ) : null}
          {/* Inspection status badge — aligned with TeamMemberSmartTaskTab and TeamMemberVehicleInspectionsTab */}
          {!isNonDriver && !inspectionStatus.isNotRequired && (
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                inspectionStatus.tone === 'red'
                  ? isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'
                  : inspectionStatus.tone === 'green'
                    ? isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                    : inspectionStatus.tone === 'amber'
                      ? isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'
                      : inspectionStatus.tone === 'blue'
                        ? isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                        : isDark ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700'
              }`}
              title={inspectionStatus.detail}
            >
              <ClipboardCheck className="h-3 w-3" />
              {inspectionStatus.label}
            </span>
          )}
          {/* Admin override button — appears when inspection is required (pending). Drivers only. */}
          {!isNonDriver && inspectionStatus.isRequired && currentVehicleId && (
            <button
              type="button"
              onClick={handleAuthorizeInspectionOverride}
              disabled={inspectionOverrideBusy}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                isDark
                  ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
              title="Authorize inspection override — allows the driver to skip the take-on inspection"
            >
              {inspectionOverrideBusy ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Shield className="h-3 w-3" />
              )}
              Override Inspection
            </button>
          )}
          {!isNonDriver && currentVehicleId ? (
            <button
              type="button"
              onClick={handleOpenVehicle}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${isDark ? 'bg-slate-800 text-slate-200 hover:bg-slate-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              title="Open linked vehicle"
            >
              <ExternalLink className="h-3 w-3" />
              Open vehicle
            </button>
          ) : null}
          {smartTaskControlOverrideState ? (
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
              <Activity className="h-3 w-3" />
              {smartTaskControlOverrideState}
            </span>
          ) : null}
          {toNonEmptyString(currentOperations?.clock_in_location) ? (
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
              <MapPin className="h-3 w-3" />
              Clocked in on site
            </span>
          ) : null}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <WorkflowMetricCard
            label="Controlled item"
            value={smartTaskControlAssignment}
            detail={currentItem?.title || toNonEmptyString(currentOperations?.headline) || null}
            isDark={isDark}
          />
          <WorkflowMetricCard
            label="Live vehicle"
            value={isNonDriver
              ? 'Non-driver'
              : (currentVehicleRegistration || 'No vehicle linked')}
            detail={isNonDriver
              ? (travelTrackingEnabled ? 'Travel time is tracked' : 'Travel time is not tracked')
              : (currentItemLocation || toNonEmptyString(currentOperations?.detail) || null)}
            isDark={isDark}
          />
          <WorkflowMetricCard
            label="Attendance"
            value={attendanceStatusLabel || 'Unknown'}
            detail={formatDateTime(currentOperations?.clocked_in_at) || toNonEmptyString(currentOperations?.clock_in_location) || null}
            isDark={isDark}
          />
          <WorkflowMetricCard
            label="Van stock"
            value={vanStockSummary ? `${vanStockSummary.totalQuantity} units` : 'No van yet'}
            detail={vanStockSummary
              ? `${vanStockSummary.itemCount} item${vanStockSummary.itemCount === 1 ? '' : 's'} in ${vanStockSummary.warehouseName}${vanStockSummary.provisionalCount > 0 ? ` · ${vanStockSummary.provisionalCount} field purchase${vanStockSummary.provisionalCount === 1 ? '' : 's'} pending` : ''}`
              : 'Provisioned on first stock issue or purchase'}
            isDark={isDark}
          />
          {locationVisible ? (
            <WorkflowMetricCard
              label="Last known location"
              value={lastKnownLocationDisplay || 'Unavailable'}
              detail={lastKnownLocationUpdatedAt ? `Updated ${lastKnownLocationUpdatedAt}` : null}
              isDark={isDark}
            />
          ) : null}
        </div>

        {teamMemberUserId && locationVisible ? (
          <div className="mt-3 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => handleRequestLocation()}
              disabled={isRequestingLocation || isRequestingLocationWakeUp}
              title="Silently fetch the worker's current location over their live connection"
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                isDark
                  ? 'bg-blue-600/20 text-blue-300 hover:bg-blue-600/30 disabled:opacity-50'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50'
              }`}
            >
              {isRequestingLocation ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Navigation className="h-3.5 w-3.5" />
              )}
              {isRequestingLocation ? 'Sending...' : 'Request Location'}
            </button>
            <button
              type="button"
              onClick={() => handleRequestLocation(true)}
              disabled={isRequestingLocation || isRequestingLocationWakeUp}
              title="Send a push notification that wakes the worker's app — use when the silent request doesn't respond"
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                isDark
                  ? 'bg-amber-600/20 text-amber-300 hover:bg-amber-600/30 disabled:opacity-50'
                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50'
              }`}
            >
              {isRequestingLocationWakeUp ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <BellRing className="h-3.5 w-3.5" />
              )}
              {isRequestingLocationWakeUp ? 'Sending...' : 'Wake & Request'}
            </button>
          </div>
        ) : null}

        {hasEffectiveLocation ? (
          <div className="mt-4">
            <TeamMemberLocationMap
              latitude={effectiveLocationLatitude!}
              longitude={effectiveLocationLongitude!}
              accuracy={effectiveLocationAccuracy}
              heading={effectiveLocationHeading}
              label={lastKnownLocationDisplay}
              updatedAt={effectiveLocationUpdatedAt}
              isDark={isDark}
            />
          </div>
        ) : null}

        {(currentItem || travelFromLabel || travelToLabel) ? (
          <div className={`mt-4 rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <p className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Current workflow context
                </p>
                <p className={`mt-1 text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                  {currentItem?.title || toNonEmptyString(currentOperations?.headline) || 'No active appointment selected'}
                </p>
                {(currentItemReference || currentItemLocation) ? (
                  <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {[currentItemReference, currentItemLocation].filter(Boolean).join(' · ')}
                  </p>
                ) : null}
                {toNonEmptyString(currentOperations?.detail) ? (
                  <p className={`mt-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {currentOperations?.detail}
                  </p>
                ) : null}
              </div>

              {(travelFromLabel || travelToLabel) ? (
                <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 lg:min-w-[320px]">
                  {travelFromLabel ? (
                    <div className={`rounded-lg px-3 py-2 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                      <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>From</p>
                      <p className={`mt-1 text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{travelFromLabel}</p>
                    </div>
                  ) : null}
                  {travelToLabel ? (
                    <div className={`rounded-lg px-3 py-2 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                      <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>To</p>
                      <p className={`mt-1 text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{travelToLabel}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {!appointmentWorkflowContext ? (
          <div className={`mt-4 rounded-lg border border-dashed px-4 py-3 text-sm ${isDark ? 'border-gray-700 bg-gray-900/40 text-gray-400' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
            Full SmartTask actions appear here once this team member is attached to a live appointment context.
          </div>
        ) : null}
      </div>

      {/* ── SmartTask Timing Markers ── */}
      <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`}>
        <div className="flex items-start gap-2">
          <Clock className="mt-0.5 h-4 w-4 text-blue-500" />
          <div>
            <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              SmartTask timing markers
            </h3>
            <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Live timing context for this team member’s current workflow, travel, and work session.
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <WorkflowMetricCard
            label="Workflow state"
            value={toNonEmptyString(currentOperations?.workflow_state_label) || formatStatusLabel(currentOperations?.workflow_state, workItem.item_type)}
            detail={stateStartedDisplay ? `Since ${stateStartedDisplay}` : activeFor ? `${activeFor} active` : null}
            isDark={isDark}
          />
          <WorkflowMetricCard
            label="Travel started"
            value={travelStartedDisplay || '—'}
            detail={toNonEmptyString(travel?.from_label) || toNonEmptyString(travel?.to_label) || null}
            isDark={isDark}
          />
          <WorkflowMetricCard
            label="Work started"
            value={workStartedDisplay || '—'}
            detail={currentItem?.title || toNonEmptyString(currentOperations?.headline) || null}
            isDark={isDark}
          />
          <WorkflowMetricCard
            label="Break status"
            value={breakStartedDisplay || 'No active break'}
            detail={breakStartedDisplay ? 'Team member is currently paused' : null}
            isDark={isDark}
          />
        </div>
      </div>

      {/* ── Performance Summary ── */}
      <div className={`grid grid-cols-1 md:grid-cols-3 gap-3`}>
        <StatCard
          icon={Star}
          label="Performance Rating"
          value={cf.performance_avg_rating ? `${cf.performance_avg_rating}/5` : 'No reviews'}
          subValue={cf.last_review_date ? `Last reviewed: ${new Date(cf.last_review_date).toLocaleDateString()}` : undefined}
          color="bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400"
          isDark={isDark}
        />
        <StatCard
          icon={AlertTriangle}
          label="Active Warnings"
          value={cf.active_warnings_count ?? 0}
          color={
            (cf.active_warnings_count ?? 0) > 0
              ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
              : 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
          }
          isDark={isDark}
        />
        <StatCard
          icon={DollarSign}
          label="Hourly Rate"
          value={effectiveWorkItem.hourly_rate ? fmtCurrency(Number(effectiveWorkItem.hourly_rate)) : 'Not set'}
          subValue="Cost rate"
          color="bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400"
          isDark={isDark}
        />
      </div>

      {/* ── Job Manager Eligibility (admin-curated) ── */}
      {managerEligibility.canManage && (
        <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h3 className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Job Manager Eligibility
              </h3>
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Allow this team member to be assigned as the responsible manager on jobs and appointments.
              </p>
              {managerEligibility.error && (
                <p className="mt-1 text-xs text-red-500">{managerEligibility.error}</p>
              )}
            </div>
            <button
              type="button"
              disabled={managerEligibility.saving}
              onClick={() => void toggleManagerEligibility(!managerEligibility.eligible)}
              className={`shrink-0 inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                managerEligibility.eligible
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : isDark
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
            >
              {managerEligibility.saving ? 'Saving…' : managerEligibility.eligible ? 'Active Manager' : 'Not a Manager'}
            </button>
          </div>
        </div>
      )}

      {/* ── Personal Details (Collapsible sections) ── */}
      <div className={`rounded-xl border divide-y ${isDark ? 'bg-gray-800/50 border-gray-700 divide-gray-700' : 'bg-white border-gray-200 divide-gray-100'}`}>
        {/* Employment */}
        <div className="p-4">
          <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Employment Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
            <InfoRow icon={Hash} label="Employee Number" value={effectiveWorkItem.item_number !== `USR-${effectiveWorkItem.assigned_to}` ? effectiveWorkItem.item_number : null} isDark={isDark} />
            <InfoRow icon={Briefcase} label="Job Title" value={cf.job_title} isDark={isDark} />
            <InfoRow icon={Building2} label="Department" value={cf.department} isDark={isDark} />
            <InfoRow icon={Shield} label="Employment Type" value={cf.employment_type?.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())} isDark={isDark} />
            <InfoRow icon={Calendar} label="Start Date" value={cf.start_date ? new Date(cf.start_date).toLocaleDateString() : null} isDark={isDark} />
            <InfoRow icon={Calendar} label="End Date" value={cf.end_date ? new Date(cf.end_date).toLocaleDateString() : null} isDark={isDark} />
          </div>
        </div>

        {/* Emergency Contact */}
        {cf.emergency_contact_name && (
          <div className="p-4">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Emergency Contact
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
              <InfoRow icon={Heart} label="Name" value={cf.emergency_contact_name} isDark={isDark} />
              <InfoRow icon={User} label="Relationship" value={cf.emergency_contact_relationship} isDark={isDark} />
              <InfoRow icon={Phone} label="Phone" value={cf.emergency_contact_phone} isDark={isDark} />
            </div>
          </div>
        )}

        {/* Banking (masked) */}
        {cf.bank_name && (
          <div className="p-4">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Banking Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
              <InfoRow icon={Building2} label="Bank" value={cf.bank_name} isDark={isDark} />
              <InfoRow icon={Hash} label="Account" value={cf.bank_account_last4} isDark={isDark} />
              <InfoRow icon={Shield} label="Account Type" value={cf.bank_account_type?.replace(/\b\w/g, (l: string) => l.toUpperCase())} isDark={isDark} />
            </div>
          </div>
        )}

        {/* Licenses */}
        {(cf.drivers_license_number || licenses.length > 0) && (
          <div className="p-4">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Licenses & Certifications
            </h3>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_220px]">
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
                  <InfoRow icon={Car} label="Driver License" value={cf.drivers_license_number} isDark={isDark} />
                  <InfoRow icon={Calendar} label="License Expiry" value={cf.drivers_license_expiry ? new Date(cf.drivers_license_expiry).toLocaleDateString() : null} isDark={isDark} />
                </div>

                {licenses.length > 0 && (
                  <div className="space-y-2">
                    {licenses.slice(0, 3).map((license, index) => {
                      const typeLabel = toNonEmptyString(license.type_label)
                        || formatItemTypeLabel(license.license_type);
                      const status = toNonEmptyString(license.status);
                      const expiry = formatDateTime(license.expiry_date)?.split(',')[0]
                        || (license.expiry_date ? new Date(String(license.expiry_date)).toLocaleDateString() : null);

                      return (
                        <div
                          key={String(license.id || `${license.license_type || 'license'}-${index}`)}
                          className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}
                        >
                          <div className="min-w-0">
                            <p className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                              {typeLabel}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {toNonEmptyString(license.license_number) || 'Number not recorded'}
                              {expiry ? ` • Expires ${expiry}` : ''}
                            </p>
                          </div>
                          {status && (
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              status === 'expired'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                : status === 'expiring_soon'
                                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            }`}>
                              {status.replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {licenses.length > 3 && (
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        +{licenses.length - 3} more on the Licenses tab
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className={`rounded-xl border p-3 ${isDark ? 'border-gray-700 bg-gray-900/60' : 'border-gray-200 bg-gray-50'}`}>
                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Compliance snapshot
                </p>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Total</span>
                    <span className={isDark ? 'text-gray-100' : 'text-gray-900'}>{licenseSummary?.total ?? licenses.length}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Driving</span>
                    <span className={isDark ? 'text-gray-100' : 'text-gray-900'}>{licenseSummary?.driving ?? (primaryDriverLicense ? 1 : 0)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Expiring soon</span>
                    <span className="text-amber-500">{licenseSummary?.expiring_soon ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>Expired</span>
                    <span className="text-red-500">{licenseSummary?.expired ?? 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        {cf.notes && (
          <div className="p-4">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Notes
            </h3>
            <p className={`text-sm whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {cf.notes}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
